import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";

export default function Expenses() {
  const { companyId, cur } = useCompany();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category: "", amount: 0, method: "cash", provider: "orange_money", date: "" });

  const load = () => api.get("/expenses", { params: { company_id: companyId } }).then(({ data }) => setItems(data.expenses));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, amount: parseFloat(form.amount), provider: form.method === "mobile_money" ? form.provider : null };
      await api.post("/expenses", body, { params: { company_id: companyId } });
      toast.success("Dépense enregistrée + comptabilisée"); setOpen(false);
      setForm({ category: "", amount: 0, method: "cash", provider: "orange_money", date: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Dépenses" subtitle="Enregistrez vos charges et suivez la trésorerie." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-expense-button"><Plus className="w-4 h-4 mr-1" /> Nouvelle dépense</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display">Nouvelle dépense</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Catégorie *</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Loyer, Électricité…" data-testid="expense-category-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Montant *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
                <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label>Payé par</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="expense-method-select">
                  <option value="cash">Espèces</option><option value="bank">Banque</option><option value="mobile_money">Mobile Money</option>
                </select></div>
              {form.method === "mobile_money" && <div className="space-y-1"><Label>Opérateur</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                  <option value="orange_money">Orange Money</option><option value="moov_money">Moov Money</option><option value="wave_money">Wave</option>
                </select></div>}
            </div>
            <DialogFooter><Button onClick={save} disabled={saving || !form.category} className="rounded-full" data-testid="save-expense-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />
      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucune dépense" />
          : <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Catégorie</TableHead><TableHead>Moyen</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
              <TableBody>{items.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(e.date)}</TableCell>
                  <TableCell className="font-medium">{e.category}</TableCell>
                  <TableCell className="text-sm">{e.method}</TableCell>
                  <TableCell className="text-right text-rose-600 font-semibold"><Money value={e.amount} cur={cur} /></TableCell>
                </TableRow>))}
              </TableBody>
            </Table>}
      </Card>
    </div>
  );
}
